import React from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route, useLocation, Navigate, useParams } from "react-router-dom";

import Nav from "@/components/layout/Nav";
import PageTransition from "@/components/layout/PageTransition";
import CookieConsent from "@/components/CookieConsent";
import CustomCursor from "@/components/CustomCursor";
import SeoHead from "@/components/seo/SeoHead";
import PageViewTracker from "@/components/seo/PageViewTracker";
import { organizationLd } from "@/components/seo/structuredData";

import Home from "@/pages/Home";
import Firm from "@/pages/Firm";
import InvestmentStrategy from "@/pages/InvestmentStrategy";
import Insights from "@/pages/Insights";
import Letter from "@/pages/Letter";
import Leadership from "@/pages/Leadership";
import GlobalPresence from "@/pages/GlobalPresence";
import InvestorPortal from "@/pages/InvestorPortal";
import Careers from "@/pages/Careers";
import Contact from "@/pages/Contact";
import Privacy from "@/pages/Privacy";
import Terms from "@/pages/Terms";
import CookiePolicy from "@/pages/legal/CookiePolicy";
import LegalNotice from "@/pages/legal/LegalNotice";
import NotFound from "@/pages/NotFound";
import AdminLogin from "@/pages/AdminLogin";
import AdminSubmissions from "@/pages/AdminSubmissions";
import AdminSubmissionDetail from "@/pages/AdminSubmissionDetail";

const LegacyLetterRedirect = () => {
  const { slug } = useParams();
  return <Navigate to={`/letters/${slug}`} replace />;
};

const DARK_HERO_PAGES = new Set([
  "/",
  "/firm",
  "/investment-strategy",
  "/insights",
  "/leadership",
  "/global-presence",
  "/investor-portal",
  "/contact",
]);

const NOINDEX_PAGES = new Set(["/investor-portal"]);

const AppShell = () => {
  const { pathname } = useLocation();
  const isAdmin = pathname.startsWith("/admin");
  const isLetterDetail = pathname.startsWith("/insights/") && pathname !== "/insights";
  const navTone = !isLetterDetail && DARK_HERO_PAGES.has(pathname) ? "dark" : "light";
  const siteNoIndex = NOINDEX_PAGES.has(pathname);

  if (isAdmin) {
    return (
      <>
        {/* Admin surfaces must not be indexed */}
        <SeoHead title="Office Console" path={pathname} noIndex />
        <Routes>
          <Route path="/admin" element={<Navigate to="/admin/submissions" replace />} />
          <Route path="/admin/login" element={<AdminLogin />} />
          <Route path="/admin/submissions" element={<AdminSubmissions />} />
          <Route path="/admin/submissions/:id" element={<AdminSubmissionDetail />} />
          <Route path="/admin/*" element={<Navigate to="/admin/submissions" replace />} />
        </Routes>
      </>
    );
  }

  return (
    <>
      {/* Site-wide Organization JSON-LD (siteWide=true → does not emit
          title/description/canonical/robots — each page owns those). */}
      <SeoHead
        siteWide
        path={pathname}
        structuredData={organizationLd()}
      />
      <PageViewTracker />
      <Nav pageTone={navTone} />
      <PageTransition>
        <Routes>
          <Route path="/" element={<Home />} />
          <Route path="/firm" element={<Firm />} />
          <Route path="/investment-strategy" element={<InvestmentStrategy />} />
          <Route path="/letters" element={<Insights />} />
          <Route path="/letters/:slug" element={<Letter />} />
          <Route path="/insights" element={<Navigate to="/letters" replace />} />
          <Route path="/insights/:slug" element={<LegacyLetterRedirect />} />
          <Route path="/leadership" element={<Leadership />} />
          <Route path="/global-presence" element={<GlobalPresence />} />
          <Route path="/investor-portal" element={<InvestorPortal />} />
          <Route path="/investors" element={<Navigate to="/investor-portal" replace />} />
          <Route path="/careers" element={<Careers />} />
          <Route path="/contact" element={<Contact />} />
          <Route path="/legal/privacy" element={<Privacy />} />
          <Route path="/legal/terms" element={<Terms />} />
          <Route path="/legal/cookies" element={<CookiePolicy />} />
          <Route path="/legal/notice" element={<LegalNotice />} />
          <Route path="/privacy" element={<Navigate to="/legal/privacy" replace />} />
          <Route path="/terms" element={<Navigate to="/legal/terms" replace />} />
          <Route path="*" element={<NotFound />} />
        </Routes>
      </PageTransition>
      <CookieConsent />
      <CustomCursor />
    </>
  );
};

function App() {
  return (
    <BrowserRouter>
      <AppShell />
    </BrowserRouter>
  );
}

export default App;
